import { Attribute, DestroyRef, Directive, ElementRef, Input, NgZone, afterNextRender, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SmoothScroll } from '@frontend/sports/common/core/feature/smooth-scroll';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { DeviceService } from '@frontend/vanilla/core';
import { ReplaySubject, tap } from 'rxjs';

interface ViewportPosition {
    scrollLeft: number;
    width: number;
}

type Mode = 'center' | 'next-item';

@Directive({
    selector: '[msScrollSelectedInView]',
    standalone: true,
})
export class ScrollSelectedInViewDirective {
    private lastSelectedItem?: string;
    private readonly destroyRef = inject(DestroyRef);
    private selectedInView = new ReplaySubject<string>(1);

    constructor(
        private element: ElementRef,
        private smoothScroll: SmoothScroll,
        private timerService: TimerService,
        private deviceService: DeviceService,
        private ngZone: NgZone,
        @Attribute('mode') private mode: Mode,
    ) {
        this.mode = this.mode || 'center';
        afterNextRender(() => {
            this.ngZone.runOutsideAngular(() => {
                this.deviceService.orientation.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onOrientationChange());
                // @TODO migrate to after render effect
                // https://angular.dev/api/core/afterRenderEffect
                this.selectedInView
                    .pipe(
                        tap((value) => this.timerService.setTimeout(() => this.selectedItemChangedHandler(value), 250)),
                        takeUntilDestroyed(this.destroyRef),
                    )
                    .subscribe();
            });
        });
    }

    @Input('msScrollSelectedInView')
    set target(value: string | undefined) {
        if (value) {
            this.selectedInView.next(value);
        }
    }

    private selectedItemChangedHandler(value: string): void {
        if ((this.deviceService.isMobile && this.lastSelectedItem === value) || (!this.deviceService.isMobile && !value)) {
            return;
        }

        this.lastSelectedItem = value;

        const scrollableContainer = this.element.nativeElement.children[0];
        if (!scrollableContainer) {
            return;
        }

        const child = scrollableContainer.querySelector(`[data-menu-item-id="${value}"]`);
        if (!child) {
            return;
        }

        const viewportPosition: ViewportPosition = {
            scrollLeft: scrollableContainer.scrollLeft,
            width: scrollableContainer.offsetWidth,
        };

        let scrollLeft;

        scrollLeft = this.mode === 'center' ? this.centerScroller(child, viewportPosition) : this.nextItemScroller(child, viewportPosition);

        this.smoothScroll.scrollLeftTo(scrollableContainer, scrollLeft, 350);
    }

    private centerScroller(child: HTMLElement, viewportPosition: ViewportPosition): number {
        const childPosition = this.elementViewportPosition(child, viewportPosition);
        const viewportMiddle = viewportPosition.width / 2;

        const childPositionMiddle = childPosition.scrollLeft + childPosition.width / 2;
        const scrollLeft = viewportPosition.scrollLeft + (childPositionMiddle - viewportMiddle);

        return Math.max(0, scrollLeft);
    }

    private nextItemScroller(child: HTMLElement, viewportPosition: ViewportPosition): number {
        const dontScroll = viewportPosition.scrollLeft;

        const nextElement = child.nextElementSibling;
        if (nextElement) {
            const nextPosition = this.elementViewportPosition(nextElement as HTMLElement, viewportPosition);
            const nextScrollLeft = nextPosition.scrollLeft + nextPosition.width - viewportPosition.width;

            if (nextScrollLeft > 0) {
                return nextScrollLeft + viewportPosition.scrollLeft;
            }
        } else {
            return child.offsetLeft;
        }

        const prevElement = child.previousElementSibling;
        if (prevElement) {
            const prevPosition = this.elementViewportPosition(prevElement as HTMLElement, viewportPosition);
            const prevScrollLeft = prevPosition.scrollLeft;

            if (prevScrollLeft < 0) {
                return prevScrollLeft + viewportPosition.scrollLeft;
            }
        } else {
            return 0;
        }

        return dontScroll;
    }

    private elementViewportPosition(element: HTMLElement, viewportPosition: ViewportPosition): ViewportPosition {
        return {
            scrollLeft: element.offsetLeft - viewportPosition.scrollLeft,
            width: element.offsetWidth,
        };
    }

    private onOrientationChange() {
        if (this.lastSelectedItem) {
            this.selectedItemChangedHandler(this.lastSelectedItem);
        }
    }
}
